<template>
  <div class="question-dialog vertical-center-dialog">
    <el-dialog
      :title="isEdit ? '编辑题目' : '添加题目'"
      :visible.sync="visible"
      width="35%"
      :append-to-body="true"
      :modal-append-to-body="false"
      :destroy-on-close="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :before-close="handleBeforeClose"
      class="vertical-center-dialog"
      custom-class="question-dialog-width-limit">
      <el-form
        ref="form"
        :model="formData"
        :rules="rules"
        label-width="100px">
        <el-form-item label="题号" prop="question_seq">
          <el-select v-model="formData.question_seq" placeholder="请选择题号">
            <el-option
              v-for="i in 10"
              :key="`T${i}`"
              :label="`T${i}`"
              :value="`T${i}`"
              :disabled="existingSeqs.includes(`T${i}`)">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="题目名称" prop="question_name">
          <el-input v-model="formData.question_name" placeholder="请输入题目名称"></el-input>
        </el-form-item>
        <el-form-item label="分数" prop="question_score">
          <el-input-number
            v-model="formData.question_score"
            :min="0"
            :max="100"
            :step="1"
            @change="handleScoreChange"
            placeholder="请输入分数">
          </el-input-number>
        </el-form-item>
        <el-form-item label="订正分数" prop="question_score_fix">
          <el-input-number
            v-model="formData.question_score_fix"
            :min="0"
            :max="100"
            :step="1"
            :disabled="formData.question_score === 100"
            placeholder="请输入订正分数">
          </el-input-number>
        </el-form-item>
        <el-form-item label="题目难度" prop="question_level">
          <el-select
            v-model="formData.question_level"
            placeholder="请选择题目难度"
            style="width: 100%">
            <el-option
              v-for="level in questionLevels"
              :key="level.value"
              :label="level.label"
              :value="level.value">
              <span :style="getQuestionLevelStyle(level.value)">{{ level.label }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="题目链接" prop="question_url">
          <el-input v-model="formData.question_url" placeholder="请输入题目链接"></el-input>
        </el-form-item>
        <el-form-item label="备注" prop="note">
          <el-input
            type="textarea"
            v-model="formData.note"
            :rows="3"
            placeholder="请输入备注">
          </el-input>
        </el-form-item>
        <el-form-item label="错误类型">
          <el-select v-model="selectedErrorCodes" multiple filterable placeholder="请选择错误类型" style="width: 100%" :disabled="formData.question_score === 100">
            <el-option v-for="err in allErrors" :key="err.error_code" :label="err.error_name" :value="err.error_code">
              <el-tooltip :content="err.error_note" placement="right" effect="dark">
                <span>
                  <el-tag :style="getErrorOutlineStyle(err.error_code)" style="margin-right: 8px;">
                    <i class="el-icon-warning" style="margin-right: 4px;"></i>{{ err.error_name }}
                  </el-tag>
                </span>
              </el-tooltip>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="标签">
          <el-tag
            v-for="tag in formData.tags"
            :key="tag.tag_code"
            :style="{ backgroundColor: getTagColor(tag.tag_code), borderColor: getTagColor(tag.tag_code), color: 'white' }"
            closable
            @close="removeTag(tag)"
            style="margin-right: 5px; margin-bottom: 5px;">
            {{ tag.tag_name }}
          </el-tag>
          <el-button size="small" @click="showTagDialog">选择标签</el-button>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleBeforeClose">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="选择标签"
      :visible.sync="tagDialogVisible"
      append-to-body
      width="30%"
      class="vertical-center-dialog">
      <el-select
        v-model="selectedTagCodes"
        multiple
        filterable
        placeholder="请选择标签"
        style="width: 100%">
        <el-option
          v-for="tag in sortedTags"
          :key="tag.tag_code"
          :label="tag.tag_name"
          :value="tag.tag_code">
          <el-tag
            :style="{ backgroundColor: getTagColor(tag.tag_code), borderColor: getTagColor(tag.tag_code), color: 'white' }"
            style="margin-right: 8px;">
            {{ tag.tag_name }}
          </el-tag>
        </el-option>
      </el-select>
      <div style="margin-top: 10px;">
        <el-button size="small" @click="showAddTagDialog">新增标签</el-button>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="tagDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleTagSelect">确 定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="新增标签"
      :visible.sync="addTagDialogVisible"
      append-to-body
      width="30%"
      class="vertical-center-dialog">
      <el-form :model="newTag" :rules="tagRules" ref="tagForm" label-width="80px">
        <el-form-item label="标签名称" prop="tag_name">
          <el-input v-model="newTag.tag_name"></el-input>
        </el-form-item>
        <el-form-item label="备注" prop="note">
          <el-input v-model="newTag.note"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="addTagDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleAddTag">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { QUESTION_LEVELS, getQuestionLevelStyle } from '@/constants/questionLevel'
import { getErrorTagInlineStyle, getErrorOutlineStyle } from '@/constants/errorTypes'

export default {
  name: 'QuestionDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    gameCode: {
      type: String,
      required: true
    },
    question: {
      type: Object,
      default: () => null
    }
  },
  data() {
    // URL格式验证
    const validateUrl = (rule, value, callback) => {
      if (!value) {
        callback()
        return
      }
      const urlPattern = /^https?:\/\/.+/i
      if (!urlPattern.test(value)) {
        callback(new Error('请输入有效的 http/https URL'))
      } else {
        callback()
      }
    }
    
    // 题目名称验证
    const validateQuestionName = async (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入题目名称'))
        return
      }
      
      // 编辑模式下，如果名称没有改变，直接通过验证
      if (this.isEdit && this.question && value.trim() === this.question.question_name.trim()) {
        callback()
        return
      }
      
      // 只在添加新题目时检查名称是否重复
      if (!this.isEdit && this.gameCode) {
        try {
          const res = await request({
            url: `/api/game/questions/${this.gameCode}`,
            method: 'get'
          })
          
          if (res.code === 200) {
            const existingNames = res.data.map(q => q.question_name.trim().toLowerCase())
            const currentName = value.trim().toLowerCase()
            
            if (existingNames.includes(currentName)) {
              callback(new Error('该题目名称在本场比赛中已存在'))
            } else {
              callback()
            }
          } else {
            callback()
          }
        } catch (error) {
          console.error('Failed to validate question name:', error)
          callback()
        }
      } else {
        callback()
      }
    }
    
    return {
      formData: {
        game_code: this.gameCode,
        question_code: '',
        question_seq: '',
        question_name: '',
        question_score: '',
        question_score_fix: '',
        question_level: '',
        question_url: '',
        note: '',
        tags: []
      },
      existingSeqs: [],
      rules: {
        question_seq: [
          { required: true, message: '请选择题目序号', trigger: 'change' }
        ],
        question_name: [
          { required: true, message: '请输入题目名称', trigger: 'blur' },
          { validator: validateQuestionName, trigger: 'blur' }
        ],
        question_score: [
          { required: true, message: '请输入分数', trigger: 'blur' },
          { type: 'number', min: 0, max: 100, message: '分数范围为0-100', trigger: 'blur' }
        ],
        question_score_fix: [
          { required: true, message: '请输入订正分数', trigger: 'blur' },
          { type: 'number', min: 0, max: 100, message: '分数范围为0-100', trigger: 'blur' }
        ],
        question_url: [
          { validator: validateUrl, trigger: 'blur' }
        ]
      },
      isEdit: false,
      allTags: [],
      selectedTagCodes: [],
      tagDialogVisible: false,
      addTagDialogVisible: false,
      allErrors: [],
      selectedErrorCodes: [],
      newTag: {
        tag_name: '',
        note: ''
      },
      tagRules: {
        tag_name: [
          { required: true, message: '请输入标签名称', trigger: 'blur' }
        ]
      },
      tagColors: [
        // 粉色系
        '#FF69B4', // 亮粉红
        '#FF1493', // 深粉红
        '#DB7093', // 苍紫罗兰红
        '#FF4466', // 珊瑚红
        
        // 蓝色系
        '#4169E1', // 皇家蓝
        '#6495ED', // 矢车菊蓝
        '#5F9EA0', // 军校蓝
        '#48D1CC', // 中绿宝石
        
        // 绿色系
        '#3CB371', // 中海蓝绿
        '#2E8B57', // 海洋绿
        '#32CD32', // 酸橙绿
        '#228B22', // 森林绿
        
        // 紫色系
        '#9370DB', // 中紫色
        '#8A2BE2', // 紫罗兰色
        '#9932CC', // 暗兰花紫
        '#BA55D3', // 中兰花紫
        
        // 橙色系
        '#FF7F50', // 珊瑚色
        '#FF6347', // 番茄色
        '#FFA07A', // 浅鲑鱼色
        '#F4A460', // 沙褐色
        
        // 红色系
        '#DC143C', // 猩红色
        '#CD5C5C', // 印度红
        '#B22222', // 砖红色
        '#A52A2A', // 褐色
        
        // 其他深色
        '#4B0082', // 靛青色
        '#483D8B', // 暗板岩蓝
        '#8B4513', // 马鞍棕色
        '#2F4F4F'  // 暗岩灰
      ],
      questionLevels: QUESTION_LEVELS
    }
  },
  computed: {
    sortedTags() {
      return [...this.allTags].sort((a, b) => a.tag_name.localeCompare(b.tag_name))
    }
  },
  watch: {
    visible(val) {
      if (val) {
        if (!this.isEdit) {
          this.fetchExistingSeqs()
        }
        this.fetchTags()
      }
      if (!val) {
        this.resetForm()
      }
    },
    gameCode: {
      immediate: true,
      handler(val) {
        this.formData.game_code = val
        if (this.visible && !this.isEdit) {
          this.fetchExistingSeqs()
        }
      }
    },
    question: {
      immediate: true,
      handler(val) {
        if (val) {
          this.setQuestionData(val)
        }
      }
    }
  },
  created() {
    this.fetchTags()
    this.fetchErrors()
  },
  methods: {
    handleClose() {
      this.$emit('update:visible', false)
      this.resetForm()
    },
    
    handleBeforeClose() {
      this.$emit('update:visible', false)
      this.resetForm()
    },
    
    async fetchExistingSeqs() {
      try {
        const res = await request({
          url: `/api/game/questions/${this.gameCode}`,
          method: 'get'
        })
        
        if (res.code === 200) {
          this.existingSeqs = res.data.map(q => q.question_seq)
          if (!this.isEdit) {
            this.setDefaultQuestionSeq()
          }
        }
      } catch (error) {
        console.error('Failed to fetch existing sequences:', error)
        this.$message.error('获取题号列表失败')
      }
    },
    
    setDefaultQuestionSeq() {
      for (let i = 1; i <= 10; i++) {
        const seq = `T${i}`
        if (!this.existingSeqs.includes(seq)) {
          this.formData.question_seq = seq
          break
        }
      }
    },
    
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.resetFields()
      }
      this.formData = {
        game_code: this.gameCode,
        question_code: '',
        question_seq: '',
        question_name: '',
        question_score: '',
        question_score_fix: '',
        question_level: '',
        question_url: '',
        note: '',
        tags: []
      }
      this.isEdit = false
      this.selectedTagCodes = []
      this.tagDialogVisible = false
      this.addTagDialogVisible = false
      this.newTag = {
        tag_name: '',
        note: ''
      }
      if (!this.isEdit) {
        this.fetchExistingSeqs()
      }
    },
    
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
        
        const requestData = {
          ...this.formData,
          question_level: this.formData.question_level || '',
          tag_codes: this.formData.tags.map(t => t.tag_code),
          error_codes: this.selectedErrorCodes
        }
        
        let submitRes
        if (this.isEdit) {
          // 先更新题目信息
          submitRes = await request({
            url: `/api/game/question/${this.formData.question_code}`,
            method: 'put',
            data: requestData
          })
          
          if (submitRes.code === 200) {
            // 更新题目标签
            const tagRes = await request({
              url: `/api/questions/${this.formData.question_code}/tags`,
              method: 'post',
              data: { tag_codes: requestData.tag_codes }
            })
            
            if (tagRes.code !== 200) {
              throw new Error('更新标签失败')
            }
            // 覆盖错误类型
            await request({
              url: `/api/questions/${this.formData.question_code}/errors`,
              method: 'post',
              data: { error_codes: requestData.error_codes }
            })
          }
        } else {
          // 先创建题目
          submitRes = await request({
            url: '/api/game/question',
            method: 'post',
            data: requestData
          })
          
          if (submitRes.code === 200) {
            // 获取新创建的题目编号
            const questionCode = submitRes.data.question_code
            
            // 如果有选择标签，则绑定标签
            if (requestData.tag_codes && requestData.tag_codes.length > 0) {
              const tagRes = await request({
                url: `/api/questions/${questionCode}/tags`,
                method: 'post',
                data: { tag_codes: requestData.tag_codes }
              })
              
              if (tagRes.code !== 200) {
                throw new Error('绑定标签失败')
              }
            }
            // 如果有错误类型且分数<100，绑定错误
            if (requestData.error_codes && requestData.error_codes.length > 0 && this.formData.question_score < 100) {
              await request({
                url: `/api/questions/${questionCode}/errors`,
                method: 'post',
                data: { error_codes: requestData.error_codes }
              })
            }
          }
        }
        
        if (submitRes.code === 200) {
          this.$message.success(this.isEdit ? '编辑成功' : '添加成功')
          this.$emit('success', {
            ...requestData,
            game_code: this.gameCode
          })
          this.$emit('update:visible', false)
          this.resetForm()
        } else {
          throw new Error(submitRes.message || (this.isEdit ? '编辑失败' : '添加失败'))
        }
      } catch (error) {
        console.error('Failed to submit question:', error)
        if (error.response && error.response.data && error.response.data.message) {
          this.$message.error(error.response.data.message)
        } else {
          this.$message.error(error.message || (this.isEdit ? '编辑失败' : '添加失败'))
        }
      }
    },
    
    setQuestionData(question) {
      if (!question) return
      
      // 获取题目的标签数据
      const fetchQuestionTags = async () => {
        try {
          const res = await request({
            url: `/api/questions/${question.question_code}/tags`,
            method: 'get'
          })
          if (res.code === 200) {
            this.formData.tags = res.data || []
          }
        } catch (error) {
          console.error('Failed to fetch question tags:', error)
          this.$message.error('获取题目标签失败')
        }
      }
      
      this.formData = {
        game_code: this.gameCode,
        question_code: question.question_code,
        question_seq: question.question_seq,
        question_name: question.question_name,
        question_score: question.question_score,
        question_score_fix: question.question_score_fix,
        question_level: question.question_level,
        question_url: question.question_url,
        note: question.note,
        tags: [], // 初始化为空数组，等待异步获取
        errors: []
      }
      
      // 获取题目标签
      fetchQuestionTags()
      // 获取题目错误
      request({ url: `/api/questions/${question.question_code}/errors`, method: 'get' })
        .then(res => {
          if (res.code === 200) {
            this.formData.errors = res.data || []
            this.selectedErrorCodes = (res.data || []).map(e => e.error_code)
          }
        })
        .catch(() => {})
      
      this.isEdit = true
      
      // 获取现题号列表（编辑时也需要，用于禁用其他已使用的题号）
      this.fetchExistingSeqs()
      
      // 确保表单验证状态被重置
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    
    handleScoreChange(value) {
      if (value === 100) {
        this.formData.question_score_fix = 100
        this.selectedErrorCodes = []
        this.formData.errors = []
      }
    },
    
    async fetchTags() {
      try {
        const res = await request({
          url: '/api/tags',
          method: 'get'
        })
        if (res.code === 200) {
          this.allTags = res.data
        }
      } catch (error) {
        console.error('Failed to fetch tags:', error)
        this.$message.error('获取标签列表失败')
      }
    },
    async fetchErrors() {
      try {
        const res = await request({ url: '/api/error-codes', method: 'get' })
        if (res.code === 200) {
          this.allErrors = res.data || []
        }
      } catch (error) {
        console.error('Failed to fetch errors:', error)
      }
    },
    
    getTagColor(tagCode) {
      // 使用与 CSS 中相同的颜色
      const colors = {
        'tag-color-1': '#409EFF',  // Element UI 主蓝色
        'tag-color-2': '#67C23A',  // Element UI 成功绿色
        'tag-color-3': '#E6A23C',  // Element UI 警告黄色
        'tag-color-4': '#F56C6C',  // Element UI 危险红色
        'tag-color-5': '#8E44AD',  // 深紫色
        'tag-color-6': '#16A085',  // 深青色
        'tag-color-7': '#D35400',  // 深橙色
        'tag-color-8': '#2980B9',  // 深蓝色
        'tag-color-9': '#27AE60',  // 深绿色
        'tag-color-10': '#C0392B'  // 深红色
      };
      const colorClass = this.getTagColorClass(tagCode);
      return colors[colorClass];
    },
    
    showTagDialog() {
      this.selectedTagCodes = this.formData.tags.map(t => t.tag_code)
      this.fetchTags()
      this.tagDialogVisible = true
    },
    
    handleTagSelect() {
      const selectedTags = this.selectedTagCodes.map(code => {
        return this.allTags.find(tag => tag.tag_code === code)
      })
      this.formData.tags = selectedTags
      this.tagDialogVisible = false
    },
    
    removeTag(tag) {
      const index = this.formData.tags.findIndex(t => t.tag_code === tag.tag_code)
      if (index > -1) {
        this.formData.tags.splice(index, 1)
        const codeIndex = this.selectedTagCodes.indexOf(tag.tag_code)
        if (codeIndex > -1) {
          this.selectedTagCodes.splice(codeIndex, 1)
        }
      }
    },
    
    showAddTagDialog() {
      this.addTagDialogVisible = true
    },
    
    async handleAddTag() {
      this.$refs.tagForm.validate(async valid => {
        if (valid) {
          try {
            const res = await request({
              url: '/api/tags',
              method: 'post',
              data: this.newTag
            })
            if (res.code === 200) {
              this.$message.success('添加标签成功')
              await this.fetchTags()
              this.addTagDialogVisible = false
              this.$refs.tagForm.resetFields()
            }
          } catch (error) {
            this.$message.error('添加标签失败')
          }
        }
      })
    },
    
    getTagColorClass(tagCode) {
      const colors = [
        'tag-color-1', 'tag-color-2', 'tag-color-3', 'tag-color-4', 'tag-color-5',
        'tag-color-6', 'tag-color-7', 'tag-color-8', 'tag-color-9', 'tag-color-10'
      ];
      const index = parseInt(tagCode.split('_')[1], 36) % colors.length;
      return colors[index];
    },
    
    getQuestionLevelStyle,
    getErrorTagInlineStyle,
    getErrorOutlineStyle
  }
}
</script>

<style lang="scss" scoped>
.el-input-number {
  width: 100%;
}

.el-select {
  width: 100%;
}

.el-dialog {
  .el-form {
    max-height: 65vh;
    overflow-y: auto;
  }
}

.question-dialog {
  :deep(.el-tag) {
    margin-right: 5px;
    margin-bottom: 5px;
    border: none;
    color: white !important;
    
    &.tag-color-1 { background-color: #409EFF !important; border-color: #409EFF !important; }  // Element UI 主蓝色
    &.tag-color-2 { background-color: #67C23A !important; border-color: #67C23A !important; }  // Element UI 成功绿色
    &.tag-color-3 { background-color: #E6A23C !important; border-color: #E6A23C !important; }  // Element UI 警告黄色
    &.tag-color-4 { background-color: #F56C6C !important; border-color: #F56C6C !important; }  // Element UI 危险红色
    &.tag-color-5 { background-color: #8E44AD !important; border-color: #8E44AD !important; }  // 深紫色
    &.tag-color-6 { background-color: #16A085 !important; border-color: #16A085 !important; }  // 深青色
    &.tag-color-7 { background-color: #D35400 !important; border-color: #D35400 !important; }  // 深橙色
    &.tag-color-8 { background-color: #2980B9 !important; border-color: #2980B9 !important; }  // 深蓝色
    &.tag-color-9 { background-color: #27AE60 !important; border-color: #27AE60 !important; }  // 深绿色
    &.tag-color-10 { background-color: #C0392B !important; border-color: #C0392B !important; } // 深红色
    
    .el-tag__close {
      color: white !important;
      background-color: transparent !important;
      
      &:hover {
        background-color: rgba(255, 255, 255, 0.2) !important;
        color: white !important;
      }
    }
  }
  
  :deep(.el-select-dropdown__item) {
    .el-tag {
      margin-right: 8px;
    }
  }
}

/* 大纲掌握进度详情对话框样式 */
:deep(.detail-statistics-dialog) {
  .el-dialog__body {
    padding: 20px;
  }

  .statistics-content {
    .el-table {
      th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
        font-size: 14px;
        padding: 8px 0;
      }

      td {
        padding: 8px 0;
      }
    }

    /* 级别标签样式 */
    .custom-level-tag {
      display: inline-block;
      padding: 0 8px;
      height: 22px;
      line-height: 22px;
      font-size: 12px;
      border-radius: 4px;
      color: white;
      width: 60px;
      text-align: center;
      white-space: nowrap;
    }
    
    .entry-level-tag {
      background-color: #67C23A;
      border-color: #67C23A;
    }
    
    .advanced-level-tag {
      background-color: #409EFF;
      border-color: #409EFF;
    }
    
    .noi-level-tag {
      background-color: #8E44AD;
      border-color: #8E44AD;
    }

    /* 进度条样式 */
    .progress-cell {
      display: flex;
      align-items: center;
      
      .progress-bar {
        flex: 1;
        height: 8px;
        background-color: #ebeef5;
        border-radius: 4px;
        overflow: hidden;
        margin-right: 10px;
        
        .progress-inner {
          height: 100%;
          transition: width 0.6s ease;
        }
      }
      
      .progress-value {
        width: 45px;
        text-align: right;
        font-weight: bold;
      }
    }
  }
}

/* 表格中用于级别的标签样式 */
.level-tag {
  display: inline-block;
  height: 24px;
  line-height: 24px;
  padding: 0 8px;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
  color: white;
  text-align: center;
  box-sizing: border-box;
  border: 1px solid transparent;
}

/* 入门级标签 - 绿色 */
.entry-level {
  background-color: #67C23A;
  border-color: #67C23A;
}

/* 提高级标签 - 蓝色 */
.advanced-level {
  background-color: #409EFF;
  border-color: #409EFF;
}

/* NOI级标签 - 紫色 */
.noi-level {
  background-color: #8E44AD;
  border-color: #8E44AD;
}

/* 状态选择对话框样式 */
:deep(.status-dialog) {
  .el-dialog__body {
    padding: 20px;
  }

  .status-options {
    margin-bottom: 20px;
  }

  .status-radio-group {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 8px;
  }

  .status-radio-item {
    margin: 0;
    flex: 1;
  }

  .el-radio {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 0;
    padding: 8px 4px;
    width: 100%;
    border-radius: 4px;
    transition: all 0.3s;
    
    &:hover {
      background-color: #f5f7fa;
    }
    
    &.is-checked {
      background-color: #ecf5ff;
    }

    .el-radio__label {
      padding-left: 0;
      margin-top: 4px;
    }

    .custom-tag {
      margin-left: 0;
      width: 60px;
      height: 24px;
      text-align: center;
      border-radius: 4px;
      font-size: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: white;

      &.mastered {
        background-color: #8E44AD;
      }
      
      &.understand {
        background-color: #67C23A;
      }
      
      &.apply {
        background-color: #409EFF;
      }
      
      &.unknown {
        background-color: #F56C6C;
      }
      
      &.aware {
        background-color: #E6A23C;
      }
      
      &.undefined {
        background-color: #909399;
      }
    }
  }
}
</style>

<style lang="scss">
// 全局样式 - 限制对话框最大宽度
.el-dialog__wrapper .question-dialog-width-limit {
  width: 35% !important;
  max-width: 600px !important;
  margin: 0 auto !important;
}

.question-dialog-width-limit.el-dialog {
  width: 100% !important;
}
</style>
 